/* Error boundary global — capture toutes les exceptions non gérées du sous-arbre
 * React et affiche un fallback utilisateur amical. Sans ce composant, React
 * (en dev) montre l'ErrorOverlay rouge natif ; (en prod) écran blanc.
 *
 * Le message affiché est identique en dev et en prod pour rester lisible côté
 * utilisateur. Le détail technique est loggé dans la console pour debug.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log technique dans la console pour permettre le debug + remontée future
    // vers un service type Sentry sans effort supplémentaire.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught error:", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  goHome = () => {
    window.location.href = "/app/dashboard";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-[60vh] flex items-center justify-center px-4"
      >
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="font-heading text-2xl">Une erreur est survenue</h1>
          <p className="mt-2 text-muted-foreground">
            Le service est momentanément indisponible. Réessayez dans quelques instants.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button onClick={this.reset} data-testid="error-boundary-retry">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Réessayer
            </Button>
            <Button variant="outline" onClick={this.goHome} data-testid="error-boundary-home">
              <Home className="h-4 w-4 mr-1.5" /> Retour à l&apos;accueil
            </Button>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Si le problème persiste, écrivez-nous à{" "}
            <a href="mailto:contact@fonbex.fr" className="underline">contact@fonbex.fr</a>.
          </p>
        </div>
      </div>
    );
  }
}
